import { ApiError } from './api';

/**
 * Firebase Authentication for the shared game account (same Firebase project as the other
 * game, giang-ho-tranh-ba-562b1), spoken over the Identity Toolkit REST API so the client
 * needs no extra SDK bundle. Sign-in returns a Firebase ID token, which the game server
 * verifies (POST /api/auth/firebase) before issuing its own session cookie.
 */
const API_KEY = 'AIzaSyBAwUl_0ZhFRcS6QFEhFUfuTt0n39dv5r8';
const IDENTITY = 'https://identitytoolkit.googleapis.com/v1';

interface FirebaseAuthResponse {
  idToken: string;
  email: string;
  localId: string;
}

async function firebaseCall<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${IDENTITY}/${path}?key=${API_KEY}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the sign-in service. Check your connection.');
  }
  const data = (await res.json().catch(() => null)) as (T & { error?: { message?: string } }) | null;
  if (!res.ok) {
    const code = data?.error?.message ?? 'UNKNOWN';
    throw firebaseError(code);
  }
  return data as T;
}

function firebaseError(code: string): ApiError {
  switch (code) {
    case 'EMAIL_EXISTS':
      return new ApiError(400, 'email_taken', 'An account with this email already exists.', {
        email: 'Already registered',
      });
    case 'EMAIL_NOT_FOUND':
    case 'INVALID_PASSWORD':
    case 'INVALID_LOGIN_CREDENTIALS':
      return new ApiError(401, 'invalid_credentials', 'Email or password is incorrect.');
    case 'USER_DISABLED':
      return new ApiError(403, 'user_disabled', 'This account has been disabled.');
    case 'TOO_MANY_ATTEMPTS_TRY_LATER':
      return new ApiError(429, 'rate_limited', 'Too many attempts. Please wait a minute and try again.');
    case 'WEAK_PASSWORD : Password should be at least 6 characters':
    case 'WEAK_PASSWORD':
      return new ApiError(400, 'weak_password', 'Password must be at least 6 characters.', {
        password: 'Too weak',
      });
    case 'INVALID_EMAIL':
      return new ApiError(400, 'invalid_email', 'Enter a valid email address.', {
        email: 'Invalid email',
      });
    default:
      return new ApiError(400, code.toLowerCase(), 'Sign-in failed. Please try again.');
  }
}

/** Creates a Firebase account and returns its ID token. */
export async function firebaseSignUp(email: string, password: string): Promise<string> {
  const r = await firebaseCall<FirebaseAuthResponse>('accounts:signUp', {
    email,
    password,
    returnSecureToken: true,
  });
  return r.idToken;
}

/** Signs in to Firebase and returns the ID token. */
export async function firebaseSignIn(email: string, password: string): Promise<string> {
  const r = await firebaseCall<FirebaseAuthResponse>('accounts:signInWithPassword', {
    email,
    password,
    returnSecureToken: true,
  });
  return r.idToken;
}

/** Sends a Firebase password-reset email. */
export async function firebaseSendPasswordReset(email: string): Promise<void> {
  await firebaseCall('accounts:sendOobCode', { requestType: 'PASSWORD_RESET', email });
}
